// SafeLine Pickup - Modern Orange Light Theme Color Scheme
export const safeLineColors = {
  // Primary Orange Palette
  primary: {
    flame: '#ff6b35',      // Main brand color, buttons, CTAs
    bright: '#ff9500',     // Hover states, secondary actions
    deep: '#e55100',       // Active states, pressed buttons
    tint: '#fff4f0',       // Light backgrounds, subtle highlights
  },
  
  // Light Theme Base
  backgrounds: {
    pure: '#ffffff',       // Main app backgrounds
    light: '#f8f9fa',      // Card backgrounds, panels
    cool: '#e9ecef',       // Borders, dividers
    medium: '#dee2e6',     // Input fields, secondary surfaces
  },
  
  // Success States (Pickup Completed)
  success: {
    emerald: '#10b981',    // Success messages, completed pickups
    light: '#d1fae5',      // Success background highlights
    forest: '#047857',     // Success button hover
  },
  
  // Warning States (Attention Needed)
  warning: {
    amber: '#f59e0b',      // Pending pickups, processing
    light: '#fef3c7',      // Warning background highlights
    dark: '#d97706',       // Warning button hover
  },
  
  // Error States (Issues/Alerts)
  error: {
    red: '#ef4444',        // Error messages, failed scans
    light: '#fee2e2',      // Error background highlights
    dark: '#dc2626',       // Error button hover
  },
  
  // Text Colors
  text: {
    darkCharcoal: '#1f2937',    // Primary text on light backgrounds
    mediumCharcoal: '#374151',  // Secondary text, labels
    lightCharcoal: '#6b7280',   // Placeholder text, disabled states
    mutedGray: '#9ca3af',       // Timestamps, captions
  }
};

// Dark theme overrides
export const darkThemeColors = {
  backgrounds: {
    pure: '#0f172a',        // Dark navy
    light: '#1e293b',       // Slightly lighter navy
    cool: '#334155',        // Medium slate
    medium: '#475569',      // Lighter slate
  },
  text: {
    darkCharcoal: '#f8fafc',      // Light text for dark bg
    mediumCharcoal: '#e2e8f0',    // Medium light text
    lightCharcoal: '#cbd5e1',     // Muted light text
    mutedGray: '#94a3b8',         // Very muted text
  }
};

// Generate CSS custom properties
export const generateCSSVariables = (isDark = false) => {
  const colors = isDark ? { ...safeLineColors, ...darkThemeColors } : safeLineColors;
  
  const cssVars = {};
  
  // Flatten color object into CSS variables
  Object.keys(colors).forEach(category => {
    Object.keys(colors[category]).forEach(shade => {
      cssVars[`--color-${category}-${shade}`] = colors[category][shade];
    });
  });
  
  // Add semantic variables
  cssVars['--color-primary'] = colors.primary.flame;
  cssVars['--color-primary-hover'] = colors.primary.bright;
  cssVars['--color-primary-active'] = colors.primary.deep;
  cssVars['--color-background'] = colors.backgrounds.pure;
  cssVars['--color-surface'] = colors.backgrounds.light;
  cssVars['--color-border'] = colors.backgrounds.cool;
  cssVars['--color-text-primary'] = colors.text.darkCharcoal;
  cssVars['--color-text-secondary'] = colors.text.mediumCharcoal;
  cssVars['--color-text-muted'] = colors.text.lightCharcoal;
  
  // Add shadows
  cssVars['--shadow-sm'] = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
  cssVars['--shadow-md'] = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
  cssVars['--shadow-lg'] = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
  cssVars['--shadow-xl'] = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
  
  // Add border radius
  cssVars['--radius-sm'] = '4px';
  cssVars['--radius-md'] = '8px';
  cssVars['--radius-lg'] = '12px';
  cssVars['--radius-xl'] = '16px';
  
  return cssVars;
};

// Apply CSS variables to document
export const applyCSSVariables = (isDark = false) => {
  const variables = generateCSSVariables(isDark);
  const root = document.documentElement;
  
  Object.keys(variables).forEach(property => {
    root.style.setProperty(property, variables[property]);
  });
};
